import { css } from '@emotion/css'
import { FormattedMessage, FormattedDate } from 'react-intl'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { CsHideIcon } from '../../legacy-design-system/icons'
import React from 'react'
import { MetadataFiltersSummary } from '../session-metadata'
import { DateRangeOption } from '../../legacy-design-system/date-picker'
import { DateRange } from '../../legacy-design-system/date-picker/shared'
import { MetadataFilter } from '../filter-manager/metadata-filter-manager'
import { Link } from '@contrast-security-inc/design-system-typography'
import {
  ToggleButton,
  ToggleButtonGroup
} from '@contrast-security-inc/design-system-buttons'

export const dateFormat: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: '2-digit',
  hour: 'numeric',
  minute: 'numeric'
}

const i18nConfig: Record<DateRangeOption, string> = {
  [DateRangeOption.LAST_DAY]: 'detail_vulnerabilities_nVulnsInlLastDay',
  [DateRangeOption.LAST_7_DAYS]: 'detail_vulnerabilities_nVulnsInLastSevenDays',
  [DateRangeOption.LAST_30_DAYS]:
    'detail_vulnerabilities_nVulnsInLastThirtyDays',
  [DateRangeOption.LAST_12_MONTHS]:
    'detail_vulnerabilities_nVulnsInLastTwelveMonths',
  [DateRangeOption.CUSTOM]: 'detail_vulnerabilities_nVulnsInCustomDateRange'
}

export interface TabItemProps<TItemData> {
  readonly id: TItemData
  readonly label: string
}

export const chartHeight = 300
export const chartContainerHeight = chartHeight + 40

interface ChartContainerProps<TChartVisualization> {
  readonly onCollapse: () => void
  readonly chartVisualizationConfig: Array<TabItemProps<TChartVisualization>>
  readonly chartVisualization: TChartVisualization
  readonly onChartVisualizationToggle: (
    chartVisualization: TChartVisualization
  ) => void
  readonly chart?: React.ReactNode
  readonly sessionMetadataSelections?: string[]
  readonly dateRangeOption?: DateRangeOption
  readonly dateRange?: DateRange
  readonly newVulnsCount: number
  readonly loadingSnapshots?: boolean
}

export function ChartContainer<TChartVisualization>({
  onCollapse,
  chartVisualizationConfig,
  chartVisualization,
  onChartVisualizationToggle,
  chart,
  sessionMetadataSelections,
  dateRangeOption,
  dateRange,
  newVulnsCount,
  loadingSnapshots = false
}: ChartContainerProps<TChartVisualization>) {
  return (
    <div
      className={css`
        width: 100%;
        height: ${chartContainerHeight}px;
      `}
    >
      {!loadingSnapshots && (
        <div>
          <div
            className={css`
              display: flex;
              align-items: baseline;
              margin-left: 42px;
              margin-right: 20px;
            `}
          >
            <div
              className={css`
                max-width: 50%;
                align-items: center;
                margin-top: 8px;
              `}
            >
              <MetadataFiltersSummary
                filters={sessionMetadataSelections}
                vulnsCount={newVulnsCount}
                vulnsCountLabel={
                  !!dateRangeOption &&
                  (!!dateRange ? (
                    <FormattedMessage
                      id={i18nConfig[dateRangeOption]}
                      values={{
                        count: newVulnsCount,
                        startDate: (
                          <FormattedDate
                            value={dateRange.start.toString()}
                            {...dateFormat}
                          />
                        ),
                        endDate: (
                          <FormattedDate
                            value={dateRange.end.toString()}
                            {...dateFormat}
                          />
                        )
                      }}
                    />
                  ) : (
                    dateRangeOption !== DateRangeOption.CUSTOM && (
                      <FormattedMessage
                        id={i18nConfig[dateRangeOption]}
                        values={{
                          count: newVulnsCount
                        }}
                      />
                    )
                  ))
                }
              />
            </div>
            <span
              className={css`
                display: inline-flex;
                flex: 1;
                justify-content: flex-end;
                align-items: center;
                margin-left: 20px;
              `}
            >
              <ToggleButtonGroup<TChartVisualization>
                value={chartVisualization}
                className={css`
                  height: 25px;
                `}
                onChange={onChartVisualizationToggle}
              >
                {chartVisualizationConfig.map((item) => (
                  <ToggleButton<TChartVisualization>
                    key={item.label}
                    value={item.id}
                    className={css`
                      font-size: 14px;
                    `}
                  >
                    {item.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <FormattedMessage id="action_hide">
                {(msg) => (
                  <Link
                    className={css`
                      margin-left: 20px;
                    `}
                    onClick={onCollapse}
                  >
                    {msg}
                  </Link>
                )}
              </FormattedMessage>
              <div
                onClick={onCollapse}
                className={css`
                  color: ${theme.color.neutral60};
                  cursor: pointer;
                `}
              >
                <CsHideIcon height="2em" width="2em" />
              </div>
            </span>
          </div>
          {!!chart && chart}
        </div>
      )}
    </div>
  )
}

export const getSessionMetadataSelectionsValues = (
  sessionMetadataSelections: MetadataFilter[]
): string[] => {
  const sessionMetadataValues: string[] = []

  sessionMetadataSelections.forEach(({ values }) =>
    sessionMetadataValues.push(...values)
  )

  return sessionMetadataValues
}
